import React from 'react';
import { ExternalLink } from 'lucide-react';
import type { Book } from '../../../../shared/types/index.js';
import { getEditionTypeInfo, getStatusBadge } from './editionTreeUtils.js';

interface EditionTimelineViewProps {
  editions: Book[];
  currentBookId: string;
  selectedNode: Book;
  onSelectNode: (book: Book) => void;
  onOpenBookDetail?: (book: Book) => void;
}

export const EditionTimelineView: React.FC<EditionTimelineViewProps> = ({
  editions,
  currentBookId,
  selectedNode,
  onSelectNode,
  onOpenBookDetail,
}) => {
  return (
    <div className="max-w-2xl mx-auto py-2">
      <div className="relative border-l-2 border-indigo-200 ml-3 sm:ml-4 pl-4 sm:pl-6 space-y-4 sm:space-y-5">
        {(editions || []).map((item, idx) => {
          const isCurrent = item.id === currentBookId;
          const isSelected = selectedNode.id === item.id;
          const typeInfo = getEditionTypeInfo(item.editionType);
          const prevItem = idx > 0 ? editions[idx - 1] : null;
          const pageDiff = prevItem ? (item.pages || 0) - (prevItem.pages || 0) : 0;

          return (
            <div key={`timeline-item-${item.id || idx}-${idx}`} className="relative group">
              {/* Circle on timeline */}
              <div
                className={`absolute -left-[23px] sm:-left-[31px] top-2 w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full border-2 bg-white transition ${
                  isSelected
                    ? 'border-indigo-600 ring-4 ring-indigo-200 scale-110 bg-indigo-600'
                    : isCurrent
                    ? 'border-indigo-500 ring-2 ring-indigo-100'
                    : 'border-slate-400 group-hover:border-indigo-400'
                }`}
              ></div>

              <div
                onClick={() => onSelectNode(item)}
                className={`p-3.5 sm:p-4 rounded-xl border transition cursor-pointer ${
                  isSelected
                    ? 'bg-white border-indigo-500 shadow-md ring-2 ring-indigo-300/60'
                    : isCurrent
                    ? 'bg-white border-indigo-300 shadow-xs ring-1 ring-indigo-100'
                    : 'bg-white border-slate-200 hover:border-slate-300 shadow-xs'
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-1.5 mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <span
                      className={`px-1.5 sm:px-2 py-0.5 rounded text-[10px] sm:text-[11px] font-semibold border ${typeInfo.badge}`}
                    >
                      {typeInfo.label}
                    </span>
                    <span className="text-xs font-bold text-slate-800">
                      {item.edition || `Edisi ${idx + 1}`}
                    </span>
                    {isCurrent && (
                      <span className="text-[9px] bg-indigo-600 text-white font-bold px-1.5 py-0.2 rounded">
                        Aktif
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[11px] font-mono font-semibold text-slate-500">
                      Tahun {item.publicationYear}
                    </span>
                    {getStatusBadge(item.status)}
                  </div>
                </div>

                <h4 className="text-xs sm:text-sm font-bold text-slate-900 mb-1 leading-snug">
                  {item.title}
                </h4>

                <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-[11px] sm:text-xs text-slate-500 mb-2">
                  <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-[10px] sm:text-[11px]">
                    {item.uibn}
                  </span>
                  <span>{item.pages || 0} Halaman</span>
                  {pageDiff !== 0 && (
                    <span
                      className={`font-semibold ${
                        pageDiff > 0 ? 'text-emerald-600' : 'text-rose-600'
                      }`}
                    >
                      ({pageDiff > 0 ? `+${pageDiff}` : pageDiff} hlm)
                    </span>
                  )}
                  <span>Bahasa: {item.language || 'Indonesia'}</span>
                </div>

                {/* Explicit, readable notes in timeline */}
                {item.editionNotes ? (
                  <div className="text-[11px] sm:text-xs text-slate-700 bg-amber-50/80 p-2.5 rounded-lg border border-amber-200/80">
                    <span className="font-semibold text-amber-900 block mb-0.5">Catatan Perubahan:</span>
                    <p className="italic leading-relaxed">"{item.editionNotes}"</p>
                  </div>
                ) : (
                  <div className="text-[11px] text-slate-400 italic">
                    Belum ada catatan perubahan untuk edisi ini.
                  </div>
                )}

                <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center justify-between text-xs">
                  <span className="text-[11px] text-indigo-600 font-semibold flex items-center gap-1">
                    {isSelected ? '✓ Sedang Dipilih' : 'Klik untuk memilih'}
                  </span>
                  {onOpenBookDetail && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenBookDetail(item);
                      }}
                      className="text-slate-600 hover:text-indigo-600 text-[11px] font-medium flex items-center gap-1 cursor-pointer"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Detail Buku
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
